/*
 * AZP-7079 — the PDP's "Compatible Models" table.
 *
 * The rows are not in the page. A part fits 1,256 appliance models on average and
 * 13,941 at the worst, so printing them into Liquid would add megabytes to the HTML,
 * and a metafield list caps at 128 entries long before that. They live instead in
 * `part-<handle>-<n>.json`, published next to the index AZP-7008 already builds:
 *
 *   {"h": handle, "page": 1, "pages": 14, "total": 13941,
 *    "b": ["Kenmore", ...],           brands, listed once
 *    "t": ["Refrigerator", ...],      appliance types, listed once
 *    "m": [["10651042101", 0, 0], ...]  model number + index into b and t
 *
 * A product with no models gets no file at all — that 404 is the empty state the
 * design asks for, not a failure. The first page is fetched when the block scrolls
 * into view, never on page load, rows reach the DOM fifty at a time as the table is
 * scrolled, and the next page is fetched only once those run out.
 *
 * Strings come in on data attributes rather than a global, because the block renders
 * only on the product page and `| t` has already run by the time they get here.
 */
(function () {
  "use strict";

  // Rows per scroll. The whole list is never in the DOM: the longest is 13,941 rows,
  // far past what anyone scrolls, and each one is three cells.
  var CHUNK = 50;

  function CompatibleModels(root) {
    this.root = root;
    this.base = root.dataset.indexBase;
    this.version = root.dataset.indexVersion || "";
    this.handle = root.dataset.handle;
    this.labels = {
      model: root.dataset.labelModel,
      brand: root.dataset.labelBrand,
      appliance: root.dataset.labelAppliance,
      empty: root.dataset.labelEmpty,
      error: root.dataset.labelError,
      searching: root.dataset.labelSearching,
      noMatch: root.dataset.labelNoMatch,
    };

    this.scroller = root.querySelector("[data-compatible-models-scroll]");
    this.status = root.querySelector("[data-compatible-models-status]");
    this.search = root.querySelector("[data-compatible-models-search]");

    this.rows = []; // every row fetched so far
    this.view = this.rows; // what the table is showing: all of them, or the matches
    this.total = 0;
    this.pages = 1;
    this.loaded = 0; // pages fetched
    this.shown = 0; // how far into `view` the DOM goes
    this.query = "";
    this.busy = false;
    this.started = false;
  }

  CompatibleModels.prototype.url = function (name) {
    var url = this.base + name;
    return this.version ? url + "?v=" + encodeURIComponent(this.version) : url;
  };

  /* Resolves to null for both "no such file" and "the network said no". The caller
   * tells them apart by which page it asked for: page 1 missing means the part has no
   * models, a later page missing means something went wrong mid-list. */
  CompatibleModels.prototype.fetchPage = function (page) {
    return fetch(this.url("part-" + this.handle + "-" + page + ".json"))
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  };

  CompatibleModels.prototype.absorb = function (data) {
    var brands = data.b || [];
    var appliances = data.t || [];
    var rows = this.rows;

    (data.m || []).forEach(function (row) {
      rows.push({
        model: row[0],
        brand: brands[row[1]] || "",
        appliance: appliances[row[2]] || "",
      });
    });

    this.total = data.total || rows.length;
    this.pages = data.pages || 1;
    this.loaded = data.page || this.loaded + 1;
  };

  CompatibleModels.prototype.start = function () {
    if (this.started) return;
    this.started = true;

    var self = this;
    this.fetchPage(1).then(function (data) {
      if (!data || !data.m || !data.m.length) {
        self.setStatus(self.labels.empty);
        return;
      }
      self.absorb(data);
      self.build();
      self.fill();
    });
  };

  CompatibleModels.prototype.setStatus = function (text) {
    if (!this.status) return;
    this.status.textContent = text;
    this.status.hidden = false;
  };

  CompatibleModels.prototype.build = function () {
    var table = document.createElement("table");

    // Columns carry the widths so this table lines up with the header bar above it:
    // under `table-layout: fixed` the first row would otherwise decide, and this
    // table's first row is a header that is taken out of the flow.
    var group = document.createElement("colgroup");
    for (var c = 0; c < 3; c++) group.appendChild(document.createElement("col"));
    table.appendChild(group);

    var head = document.createElement("thead");
    var headRow = document.createElement("tr");

    // The bar the shopper sees is drawn by the theme, above the scrolling area. This
    // header exists so the rows below are still announced with their column names.
    head.className = "visually-hidden";

    [this.labels.model, this.labels.brand, this.labels.appliance].forEach(function (label) {
      var cell = document.createElement("th");
      cell.setAttribute("scope", "col");
      cell.textContent = label;
      headRow.appendChild(cell);
    });

    head.appendChild(headRow);
    table.appendChild(head);

    this.body = document.createElement("tbody");
    table.appendChild(this.body);

    this.note = document.createElement("p");
    this.note.className = "compatible-models-note";
    this.note.hidden = true;

    if (this.status) this.status.hidden = true;
    this.scroller.appendChild(table);
    this.root.appendChild(this.note);

    var self = this;
    this.scroller.addEventListener("scroll", function () {
      self.onScroll();
    });

    if (this.search) {
      var timer = null;
      this.search.addEventListener("input", function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          self.onSearch();
        }, 200);
      });
    }
  };

  function rowElement(row) {
    var tr = document.createElement("tr");
    [row.model, row.brand, row.appliance].forEach(function (value) {
      var cell = document.createElement("td");
      cell.textContent = value;
      tr.appendChild(cell);
    });
    return tr;
  }

  /* Rows are appended, never re-rendered while the view holds still: the list runs to
   * five figures and rebuilding it on every scroll would throw away the DOM being read. */
  CompatibleModels.prototype.fill = function () {
    var end = Math.min(this.view.length, this.shown + CHUNK);
    var fragment = document.createDocumentFragment();

    for (var i = this.shown; i < end; i++) {
      fragment.appendChild(rowElement(this.view[i]));
    }

    this.shown = end;
    this.body.appendChild(fragment);
    this.measureGutter();
  };

  /* The header bar reserves the scrollbar's width so its columns line up with the
   * scrolling ones below. How wide that is depends on the platform and on whether the
   * custom scrollbar styling applied, so it is measured — and only once there are rows
   * to overflow, since an empty table has no scrollbar to measure. */
  CompatibleModels.prototype.measureGutter = function () {
    if (this.gutter) return;
    var gutter = this.scroller.offsetWidth - this.scroller.clientWidth;
    if (!gutter) return;
    this.gutter = gutter;
    this.root.style.setProperty("--cm-gutter", gutter + "px");
  };

  /* Within one frame-height of the end, the next rows are worth having: a shopper
   * reading their way down should never meet the bottom of the list. */
  CompatibleModels.prototype.onScroll = function () {
    if (this.busy) return;

    var el = this.scroller;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - el.clientHeight) return;

    // Rows already fetched go in first; only an exhausted list costs a request.
    if (this.shown < this.view.length) {
      this.fill();
      return;
    }

    // A filtered view is complete as soon as it is built — every page was read to
    // build it — so there is nothing left to fetch.
    if (this.query || this.loaded >= this.pages) return;

    var self = this;
    this.busy = true;
    this.fetchPage(this.loaded + 1).then(function (data) {
      self.busy = false;
      if (!data || !data.m) {
        // Keep what is on screen — a truncated table still answers most questions —
        // and say why it stopped rather than silently ending the list.
        self.pages = self.loaded;
        self.setNote(self.labels.error);
        return;
      }
      self.absorb(data);
      self.fill();
    });
  };

  CompatibleModels.prototype.setNote = function (text) {
    if (!this.note) return;
    if (!text) {
      this.note.hidden = true;
      return;
    }
    this.note.textContent = text;
    this.note.hidden = false;
  };

  /* Model numbers are compared without their separators, the same way the search box
   * of AZP-7008 does it: a shopper types `110875` for a machine badged `110.875`. */
  function sortKey(text) {
    return String(text || "").toUpperCase().replace(/[^A-Z0-9]+/g, "");
  }

  CompatibleModels.prototype.onSearch = function () {
    var query = sortKey(this.search.value);
    if (query === this.query) return;
    this.query = query;

    if (!query) {
      this.show(this.rows);
      return;
    }

    // Only the fetched pages are searchable, and a shopper typing a model number wants
    // an answer about the whole list — so the rest is pulled in before filtering. It is
    // the one moment worth spending the extra requests on.
    if (this.loaded < this.pages) {
      this.setNote(this.labels.searching);
      var self = this;
      this.loadAll().then(function () {
        if (self.query !== query) return; // a later keystroke won
        self.filter(query);
      });
      return;
    }

    this.filter(query);
  };

  CompatibleModels.prototype.loadAll = function () {
    var self = this;

    function next() {
      if (self.loaded >= self.pages) return Promise.resolve();
      return self.fetchPage(self.loaded + 1).then(function (data) {
        if (!data || !data.m) {
          self.pages = self.loaded; // stop asking for pages that are not there
          return;
        }
        self.absorb(data);
        return next();
      });
    }

    return next();
  };

  CompatibleModels.prototype.filter = function (query) {
    var matches = [];
    for (var i = 0; i < this.rows.length; i++) {
      if (sortKey(this.rows[i].model).indexOf(query) !== -1) matches.push(this.rows[i]);
    }

    this.show(matches);
    this.setNote(matches.length ? null : this.labels.noMatch);
  };

  /* Swaps what the table is showing and starts it over at the top. Matches scroll the
   * same way the full list does — fifty at a time — so a query that hits thousands of
   * rows costs no more to draw than one that hits ten. */
  CompatibleModels.prototype.show = function (rows) {
    this.view = rows;
    this.shown = 0;
    this.body.textContent = "";
    this.scroller.scrollTop = 0;
    if (rows === this.rows) this.setNote(null);
    this.fill();
  };

  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll("[data-compatible-models]").forEach(function (root) {
      if (!root.dataset.indexBase || !root.dataset.handle) return;

      // The script tag sits inside the block, so the Theme Editor re-injects and re-runs
      // it every time a setting changes. Without this the second run builds a second
      // table underneath the first.
      if (root.dataset.ready) return;
      root.dataset.ready = "1";

      var block = new CompatibleModels(root);

      if (!("IntersectionObserver" in window)) {
        block.start();
        return;
      }

      // 400px ahead of the viewport: far enough that the table is usually filled in by
      // the time it is read, close enough that a shopper who never scrolls down pays
      // nothing for it.
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            block.start();
          });
        },
        { rootMargin: "400px 0px" }
      );

      observer.observe(root);
    });
  });
})();
